// src/pages/Hosting.js
import React, { useState, useEffect, useCallback } from 'react';
import { Server, Globe, Database, Lock, Wifi, Plus, Edit2, Trash2, RefreshCw, AlertTriangle, Search, ChevronDown, ChevronUp, Eye, EyeOff } from 'lucide-react';
import api from '../utils/api';
import { useToast } from '../contexts/ToastContext';
import { useAuth } from '../contexts/AuthContext';

// ─── Helpers ───────────────────────────────────────────────────────────────
const STATUS_CLASS = { active: 'badge-success', inactive: 'badge-neutral', maintenance: 'badge-warning', suspended: 'badge-danger', expired: 'badge-danger', pending: 'badge-warning', transferred: 'badge-neutral', corrupted: 'badge-danger', blocked: 'badge-danger', revoked: 'badge-danger' };
const STATUS_LABEL = { active: 'Activ', inactive: 'Inactiv', maintenance: 'Mentenanță', suspended: 'Suspendat', expired: 'Expirat', pending: 'Pending', transferred: 'Transferat', corrupted: 'Corupt', blocked: 'Blocat', revoked: 'Revocat' };

function ExpiryCell({ date }) {
  if (!date) return <span style={{ color: 'var(--text-secondary)', fontSize: 12 }}>—</span>;
  const days = Math.ceil((new Date(date) - new Date()) / 86400000);
  const color = days <= 7 ? 'var(--danger)' : days <= 30 ? 'var(--warning)' : 'var(--text-primary)';
  return (
    <div>
      <div style={{ fontSize: 13 }}>{new Date(date).toLocaleDateString('ro-RO')}</div>
      <div style={{ fontSize: 11, color, fontWeight: 600 }}>{days <= 0 ? 'Expirat!' : `${days} zile`}</div>
    </div>
  );
}

function PasswordCell({ value }) {
  const [show, setShow] = useState(false);
  if (!value) return <span style={{ color: 'var(--text-secondary)' }}>—</span>;
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
      <span className="mono" style={{ fontSize: 12 }}>{show ? value : '••••••••'}</span>
      <button className="btn-icon" style={{ padding: 2, border: 'none' }} onClick={() => setShow(!show)}>
        {show ? <EyeOff size={12} /> : <Eye size={12} />}
      </button>
    </div>
  );
}

// ─── VPS Tab ──────────────────────────────────────────────────────────────
function VpsModal({ item, clients, vpsList, onClose, onSave }) {
  const [f, setF] = useState(item || { name: '', hostname: '', ip_address: '', os: 'Ubuntu 22.04 LTS', cpu_cores: 2, ram_gb: 4, disk_gb: 50, provider: '', datacenter: '', monthly_cost: '', currency: 'EUR', purchase_date: '', expiry_date: '', root_password: '', ssh_port: 22, ssh_user: 'root', notes: '', client_id: '', status: 'active' });
  const [loading, setLoading] = useState(false);
  const toast = useToast();
  const set = (k, v) => setF(p => ({ ...p, [k]: v }));

  const save = async () => {
    if (!f.name) return toast.error('Numele este obligatoriu');
    setLoading(true);
    try {
      if (item?.id) { await api.put(`/hosting/vps/${item.id}`, f); toast.success('VPS actualizat'); }
      else { await api.post('/hosting/vps', f); toast.success('VPS adăugat'); }
      onSave();
    } catch (e) { toast.error(e.response?.data?.error || 'Eroare'); }
    finally { setLoading(false); }
  };

  return (
    <div className="modal-overlay" onClick={e => e.target === e.currentTarget && onClose()}>
      <div className="modal modal-xl">
        <div className="modal-header"><span className="modal-title">{item?.id ? 'Editare VPS' : 'VPS Nou'}</span><button className="btn-icon" onClick={onClose}>×</button></div>
        <div className="modal-body">
          <div className="form-grid form-grid-3">
            <div className="form-group"><label className="form-label">Nume *</label><input className="form-input" value={f.name} onChange={e => set('name', e.target.value)} placeholder="VPS-Production-01" /></div>
            <div className="form-group"><label className="form-label">Hostname</label><input className="form-input" value={f.hostname || ''} onChange={e => set('hostname', e.target.value)} placeholder="vps01.domeniu.ro" /></div>
            <div className="form-group"><label className="form-label">IP Adresă</label><input className="form-input" value={f.ip_address || ''} onChange={e => set('ip_address', e.target.value)} placeholder="185.120.45.10" /></div>
            <div className="form-group"><label className="form-label">Sistem de Operare</label><input className="form-input" value={f.os || ''} onChange={e => set('os', e.target.value)} placeholder="Ubuntu 22.04 LTS" /></div>
            <div className="form-group"><label className="form-label">CPU (core-uri)</label><input className="form-input" type="number" value={f.cpu_cores || ''} onChange={e => set('cpu_cores', e.target.value)} /></div>
            <div className="form-group"><label className="form-label">RAM (GB)</label><input className="form-input" type="number" step="0.5" value={f.ram_gb || ''} onChange={e => set('ram_gb', e.target.value)} /></div>
            <div className="form-group"><label className="form-label">Disk (GB)</label><input className="form-input" type="number" value={f.disk_gb || ''} onChange={e => set('disk_gb', e.target.value)} /></div>
            <div className="form-group"><label className="form-label">Provider</label><input className="form-input" value={f.provider || ''} onChange={e => set('provider', e.target.value)} placeholder="Hetzner, DigitalOcean..." /></div>
            <div className="form-group"><label className="form-label">Datacenter</label><input className="form-input" value={f.datacenter || ''} onChange={e => set('datacenter', e.target.value)} placeholder="Frankfurt, Nuremberg..." /></div>
            <div className="form-group"><label className="form-label">Cost Lunar</label><input className="form-input" type="number" step="0.01" value={f.monthly_cost || ''} onChange={e => set('monthly_cost', e.target.value)} /></div>
            <div className="form-group"><label className="form-label">Monedă</label><select className="form-select" value={f.currency || 'EUR'} onChange={e => set('currency', e.target.value)}><option>EUR</option><option>USD</option><option>RON</option></select></div>
            <div className="form-group"><label className="form-label">Status</label><select className="form-select" value={f.status} onChange={e => set('status', e.target.value)}><option value="active">Activ</option><option value="inactive">Inactiv</option><option value="maintenance">Mentenanță</option><option value="suspended">Suspendat</option></select></div>
            <div className="form-group"><label className="form-label">Dată Achiziție</label><input className="form-input" type="date" value={f.purchase_date || ''} onChange={e => set('purchase_date', e.target.value)} /></div>
            <div className="form-group"><label className="form-label">Dată Expirare</label><input className="form-input" type="date" value={f.expiry_date || ''} onChange={e => set('expiry_date', e.target.value)} /></div>
            <div className="form-group"><label className="form-label">Client</label><select className="form-select" value={f.client_id || ''} onChange={e => set('client_id', e.target.value)}><option value="">— Fără client —</option>{clients.map(c => <option key={c.id} value={c.id}>{c.company_name || c.contact_name}</option>)}</select></div>
            <div className="form-group"><label className="form-label">Parola Root</label><input className="form-input" type="text" value={f.root_password || ''} onChange={e => set('root_password', e.target.value)} placeholder="Parolă root SSH" /></div>
            <div className="form-group"><label className="form-label">Port SSH</label><input className="form-input" type="number" value={f.ssh_port || 22} onChange={e => set('ssh_port', e.target.value)} /></div>
            <div className="form-group"><label className="form-label">User SSH</label><input className="form-input" value={f.ssh_user || 'root'} onChange={e => set('ssh_user', e.target.value)} /></div>
          </div>
          <div className="form-group" style={{ marginBottom: 0 }}><label className="form-label">Notițe</label><textarea className="form-textarea" value={f.notes || ''} onChange={e => set('notes', e.target.value)} /></div>
        </div>
        <div className="modal-footer"><button className="btn btn-secondary" onClick={onClose}>Anulare</button><button className="btn btn-primary" onClick={save} disabled={loading}>{loading ? <span className="spinner" /> : 'Salvare'}</button></div>
      </div>
    </div>
  );
}

function VpsTab({ clients }) {
  const [list, setList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [modal, setModal] = useState(null);
  const { hasRole } = useAuth();
  const toast = useToast();

  const load = useCallback(async () => {
    setLoading(true);
    try { const { data } = await api.get('/hosting/vps', { params: search ? { search } : {} }); setList(data); }
    catch(e) { console.error('VPS load error', e); }
    finally { setLoading(false); }
  }, [search]);

  useEffect(() => { load(); }, [load]);

  const del = async (id, name) => {
    if (!window.confirm(`Ștergi VPS-ul "${name}"?`)) return;
    try { await api.delete(`/hosting/vps/${id}`); toast.success('VPS șters'); load(); }
    catch (e) { toast.error(e.response?.data?.error || 'Eroare'); }
  };

  return (
    <>
      <div style={{ display: 'flex', gap: 10, marginBottom: 16 }}>
        <div style={{ position: 'relative', flex: 1 }}><Search size={15} style={{ position: 'absolute', left: 10, top: '50%', transform: 'translateY(-50%)', color: 'var(--text-secondary)' }} /><input className="form-input" style={{ paddingLeft: 32 }} placeholder="Caută VPS..." value={search} onChange={e => setSearch(e.target.value)} /></div>
        <button className="btn btn-secondary" onClick={load}><RefreshCw size={15} /></button>
        <button className="btn btn-primary" onClick={() => setModal('new')}><Plus size={15} /> VPS Nou</button>
      </div>
      <div className="card" style={{ padding: 0 }}>
        {loading ? <div className="loading-center"><span className="spinner" /></div> : (
          <div className="table-wrapper"><table>
            <thead><tr><th>NUME</th><th>IP / HOSTNAME</th><th>SPECS</th><th>PROVIDER</th><th>CLIENT</th><th>COST</th><th>EXPIRARE</th><th>STATUS</th><th>ACȚIUNI</th></tr></thead>
            <tbody>{list.map(v => (
              <tr key={v.id}>
                <td><div style={{ fontWeight: 600 }}>{v.name}</div><div style={{ fontSize: 11, color: 'var(--text-secondary)' }}>{v.os}</div></td>
                <td><div className="mono" style={{ fontSize: 13 }}>{v.ip_address}</div><div style={{ fontSize: 11, color: 'var(--text-secondary)' }}>{v.hostname}</div></td>
                <td style={{ fontSize: 12 }}><div>{v.cpu_cores} CPU · {v.ram_gb}GB RAM</div><div style={{ color: 'var(--text-secondary)' }}>{v.disk_gb}GB SSD</div></td>
                <td style={{ fontSize: 13 }}>{v.provider || '—'}</td>
                <td style={{ fontSize: 13 }}>{v.company_name || v.contact_name || '—'}</td>
                <td style={{ fontSize: 13 }}>{v.monthly_cost ? `${v.monthly_cost} ${v.currency}` : '—'}</td>
                <td><ExpiryCell date={v.expiry_date} /></td>
                <td><span className={`badge ${STATUS_CLASS[v.status] || 'badge-neutral'}`}>{STATUS_LABEL[v.status] || v.status}</span></td>
                <td><div style={{ display: 'flex', gap: 6 }}><button className="btn-icon" onClick={() => setModal(v)}><Edit2 size={14} /></button>{hasRole('superadmin', 'admin') && <button className="btn-icon" style={{ color: 'var(--danger)' }} onClick={() => del(v.id, v.name)}><Trash2 size={14} /></button>}</div></td>
              </tr>
            ))}</tbody>
          </table></div>
        )}
      </div>
      {modal && <VpsModal item={modal === 'new' ? null : modal} clients={clients} vpsList={list} onClose={() => setModal(null)} onSave={() => { setModal(null); load(); }} />}
    </>
  );
}

// ─── Domains Tab ──────────────────────────────────────────────────────────
function DomainModal({ item, clients, vpsList, onClose, onSave }) {
  const [f, setF] = useState(item || { domain: '', registrar: '', registration_date: '', expiry_date: '', auto_renew: 1, nameservers: '', status: 'active', monthly_cost: '', currency: 'EUR', client_id: '', vps_id: '', notes: '' });
  const [loading, setLoading] = useState(false);
  const toast = useToast();
  const set = (k, v) => setF(p => ({ ...p, [k]: v }));

  const save = async () => {
    if (!f.domain) return toast.error('Domeniul este obligatoriu');
    setLoading(true);
    try {
      if (item?.id) { await api.put(`/hosting/domains/${item.id}`, f); toast.success('Domeniu actualizat'); }
      else { await api.post('/hosting/domains', f); toast.success('Domeniu adăugat'); }
      onSave();
    } catch (e) { toast.error(e.response?.data?.error || 'Eroare'); }
    finally { setLoading(false); }
  };

  return (
    <div className="modal-overlay" onClick={e => e.target === e.currentTarget && onClose()}>
      <div className="modal modal-lg">
        <div className="modal-header"><span className="modal-title">{item?.id ? 'Editare Domeniu' : 'Domeniu Nou'}</span><button className="btn-icon" onClick={onClose}>×</button></div>
        <div className="modal-body">
          <div className="form-grid form-grid-2">
            <div className="form-group"><label className="form-label">Domeniu *</label><input className="form-input" value={f.domain} onChange={e => set('domain', e.target.value)} placeholder="exemplu.ro" /></div>
            <div className="form-group"><label className="form-label">Registrar</label><input className="form-input" value={f.registrar || ''} onChange={e => set('registrar', e.target.value)} placeholder="GoDaddy, RoTLD..." /></div>
            <div className="form-group"><label className="form-label">Data Înregistrare</label><input className="form-input" type="date" value={f.registration_date || ''} onChange={e => set('registration_date', e.target.value)} /></div>
            <div className="form-group"><label className="form-label">Data Expirare</label><input className="form-input" type="date" value={f.expiry_date || ''} onChange={e => set('expiry_date', e.target.value)} /></div>
            <div className="form-group"><label className="form-label">Status</label><select className="form-select" value={f.status} onChange={e => set('status', e.target.value)}><option value="active">Activ</option><option value="inactive">Inactiv</option><option value="expired">Expirat</option><option value="pending">Pending</option><option value="transferred">Transferat</option></select></div>
            <div className="form-group"><label className="form-label">Reînnoire Automată</label><select className="form-select" value={f.auto_renew} onChange={e => set('auto_renew', parseInt(e.target.value))}><option value={1}>Da</option><option value={0}>Nu</option></select></div>
            <div className="form-group"><label className="form-label">Cost Anual</label><input className="form-input" type="number" step="0.01" value={f.monthly_cost || ''} onChange={e => set('monthly_cost', e.target.value)} /></div>
            <div className="form-group"><label className="form-label">Monedă</label><select className="form-select" value={f.currency || 'EUR'} onChange={e => set('currency', e.target.value)}><option>EUR</option><option>USD</option><option>RON</option></select></div>
            <div className="form-group"><label className="form-label">Client</label><select className="form-select" value={f.client_id || ''} onChange={e => set('client_id', e.target.value)}><option value="">— Fără client —</option>{clients.map(c => <option key={c.id} value={c.id}>{c.company_name || c.contact_name}</option>)}</select></div>
            <div className="form-group"><label className="form-label">VPS Asociat</label><select className="form-select" value={f.vps_id || ''} onChange={e => set('vps_id', e.target.value)}><option value="">— Fără VPS —</option>{vpsList.map(v => <option key={v.id} value={v.id}>{v.name} ({v.ip_address})</option>)}</select></div>
          </div>
          <div className="form-group"><label className="form-label">Nameservere</label><input className="form-input" value={f.nameservers || ''} onChange={e => set('nameservers', e.target.value)} placeholder="ns1.exemplu.ro, ns2.exemplu.ro" /></div>
          <div className="form-group" style={{ marginBottom: 0 }}><label className="form-label">Notițe</label><textarea className="form-textarea" value={f.notes || ''} onChange={e => set('notes', e.target.value)} /></div>
        </div>
        <div className="modal-footer"><button className="btn btn-secondary" onClick={onClose}>Anulare</button><button className="btn btn-primary" onClick={save} disabled={loading}>{loading ? <span className="spinner" /> : 'Salvare'}</button></div>
      </div>
    </div>
  );
}

function DomainsTab({ clients, vpsList }) {
  const [list, setList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [modal, setModal] = useState(null);
  const { hasRole } = useAuth();
  const toast = useToast();

  const load = useCallback(async () => {
    setLoading(true);
    try { const { data } = await api.get('/hosting/domains', { params: search ? { search } : {} }); setList(data); }
    catch(e) { console.error('Domains load error', e); }
    finally { setLoading(false); }
  }, [search]);

  useEffect(() => { load(); }, [load]);

  const del = async (id, name) => {
    if (!window.confirm(`Ștergi domeniul "${name}"?`)) return;
    try { await api.delete(`/hosting/domains/${id}`); toast.success('Domeniu șters'); load(); }
    catch (e) { toast.error(e.response?.data?.error || 'Eroare'); }
  };

  return (
    <>
      <div style={{ display: 'flex', gap: 10, marginBottom: 16 }}>
        <div style={{ position: 'relative', flex: 1 }}><Search size={15} style={{ position: 'absolute', left: 10, top: '50%', transform: 'translateY(-50%)', color: 'var(--text-secondary)' }} /><input className="form-input" style={{ paddingLeft: 32 }} placeholder="Caută domenii..." value={search} onChange={e => setSearch(e.target.value)} /></div>
        <button className="btn btn-secondary" onClick={load}><RefreshCw size={15} /></button>
        <button className="btn btn-primary" onClick={() => setModal('new')}><Plus size={15} /> Domeniu Nou</button>
      </div>
      <div className="card" style={{ padding: 0 }}>
        {loading ? <div className="loading-center"><span className="spinner" /></div> : (
          <div className="table-wrapper"><table>
            <thead><tr><th>DOMENIU</th><th>REGISTRAR</th><th>CLIENT</th><th>VPS</th><th>COST</th><th>AUTO</th><th>EXPIRARE</th><th>STATUS</th><th>ACȚIUNI</th></tr></thead>
            <tbody>{list.map(d => (
              <tr key={d.id}>
                <td><span style={{ fontWeight: 600, fontSize: 14 }}>{d.domain}</span></td>
                <td style={{ fontSize: 13 }}>{d.registrar || '—'}</td>
                <td style={{ fontSize: 13 }}>{d.company_name || '—'}</td>
                <td style={{ fontSize: 13 }}>{d.vps_name || '—'}</td>
                <td style={{ fontSize: 13 }}>{d.monthly_cost ? `${d.monthly_cost} ${d.currency}` : '—'}</td>
                <td><span className={`badge ${d.auto_renew ? 'badge-success' : 'badge-neutral'}`}>{d.auto_renew ? 'Da' : 'Nu'}</span></td>
                <td><ExpiryCell date={d.expiry_date} /></td>
                <td><span className={`badge ${STATUS_CLASS[d.status] || 'badge-neutral'}`}>{STATUS_LABEL[d.status] || d.status}</span></td>
                <td><div style={{ display: 'flex', gap: 6 }}><button className="btn-icon" onClick={() => setModal(d)}><Edit2 size={14} /></button>{hasRole('superadmin', 'admin') && <button className="btn-icon" style={{ color: 'var(--danger)' }} onClick={() => del(d.id, d.domain)}><Trash2 size={14} /></button>}</div></td>
              </tr>
            ))}</tbody>
          </table></div>
        )}
      </div>
      {modal && <DomainModal item={modal === 'new' ? null : modal} clients={clients} vpsList={vpsList} onClose={() => setModal(null)} onSave={() => { setModal(null); load(); }} />}
    </>
  );
}

// ─── Databases Tab ─────────────────────────────────────────────────────────
function DatabaseModal({ item, clients, vpsList, onClose, onSave }) {
  const [f, setF] = useState(item || { name: '', type: 'mysql', host: '', port: 3306, database_name: '', username: '', password: '', size_mb: '', status: 'active', vps_id: '', client_id: '', notes: '', expiry_date: '' });
  const [loading, setLoading] = useState(false);
  const toast = useToast();
  const set = (k, v) => setF(p => ({ ...p, [k]: v }));

  const DB_PORTS = { mysql: 3306, postgresql: 5432, sqlite: null, mongodb: 27017, redis: 6379 };

  const save = async () => {
    if (!f.database_name) return toast.error('Numele bazei de date este obligatoriu');
    setLoading(true);
    try {
      if (item?.id) { await api.put(`/hosting/databases/${item.id}`, f); toast.success('DB actualizată'); }
      else { await api.post('/hosting/databases', f); toast.success('DB adăugată'); }
      onSave();
    } catch (e) { toast.error(e.response?.data?.error || 'Eroare'); }
    finally { setLoading(false); }
  };

  return (
    <div className="modal-overlay" onClick={e => e.target === e.currentTarget && onClose()}>
      <div className="modal modal-lg">
        <div className="modal-header"><span className="modal-title">{item?.id ? 'Editare Bază de Date' : 'Bază de Date Nouă'}</span><button className="btn-icon" onClick={onClose}>×</button></div>
        <div className="modal-body">
          <div className="form-grid form-grid-2">
            <div className="form-group"><label className="form-label">Etichetă</label><input className="form-input" value={f.name || ''} onChange={e => set('name', e.target.value)} placeholder="DB Main App" /></div>
            <div className="form-group"><label className="form-label">Tip</label><select className="form-select" value={f.type} onChange={e => { set('type', e.target.value); set('port', DB_PORTS[e.target.value] || ''); }}><option value="mysql">MySQL</option><option value="postgresql">PostgreSQL</option><option value="mongodb">MongoDB</option><option value="redis">Redis</option><option value="sqlite">SQLite</option></select></div>
            <div className="form-group"><label className="form-label">Host</label><input className="form-input" value={f.host || ''} onChange={e => set('host', e.target.value)} placeholder="localhost sau IP" /></div>
            <div className="form-group"><label className="form-label">Port</label><input className="form-input" type="number" value={f.port || ''} onChange={e => set('port', e.target.value)} /></div>
            <div className="form-group"><label className="form-label">Nume DB *</label><input className="form-input" value={f.database_name} onChange={e => set('database_name', e.target.value)} placeholder="myapp_production" /></div>
            <div className="form-group"><label className="form-label">Username</label><input className="form-input" value={f.username || ''} onChange={e => set('username', e.target.value)} /></div>
            <div className="form-group"><label className="form-label">Parolă</label><input className="form-input" type="text" value={f.password || ''} onChange={e => set('password', e.target.value)} /></div>
            <div className="form-group"><label className="form-label">Dimensiune (MB)</label><input className="form-input" type="number" step="0.1" value={f.size_mb || ''} onChange={e => set('size_mb', e.target.value)} /></div>
            <div className="form-group"><label className="form-label">Status</label><select className="form-select" value={f.status} onChange={e => set('status', e.target.value)}><option value="active">Activ</option><option value="inactive">Inactiv</option><option value="maintenance">Mentenanță</option><option value="corrupted">Corupt</option></select></div>
            <div className="form-group"><label className="form-label">Dată Expirare</label><input className="form-input" type="date" value={f.expiry_date || ''} onChange={e => set('expiry_date', e.target.value)} /></div>
            <div className="form-group"><label className="form-label">VPS</label><select className="form-select" value={f.vps_id || ''} onChange={e => set('vps_id', e.target.value)}><option value="">— Fără VPS —</option>{vpsList.map(v => <option key={v.id} value={v.id}>{v.name}</option>)}</select></div>
            <div className="form-group"><label className="form-label">Client</label><select className="form-select" value={f.client_id || ''} onChange={e => set('client_id', e.target.value)}><option value="">— Fără client —</option>{clients.map(c => <option key={c.id} value={c.id}>{c.company_name || c.contact_name}</option>)}</select></div>
          </div>
          <div className="form-group" style={{ marginBottom: 0 }}><label className="form-label">Notițe</label><textarea className="form-textarea" value={f.notes || ''} onChange={e => set('notes', e.target.value)} /></div>
        </div>
        <div className="modal-footer"><button className="btn btn-secondary" onClick={onClose}>Anulare</button><button className="btn btn-primary" onClick={save} disabled={loading}>{loading ? <span className="spinner" /> : 'Salvare'}</button></div>
      </div>
    </div>
  );
}

function DatabasesTab({ clients, vpsList }) {
  const [list, setList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modal, setModal] = useState(null);
  const { hasRole } = useAuth();
  const toast = useToast();

  const load = useCallback(async () => {
    setLoading(true);
    try { const { data } = await api.get('/hosting/databases'); setList(data); }
    catch(e) { console.error('DB load error', e); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const del = async (id, name) => {
    if (!window.confirm(`Ștergi baza de date "${name}"?`)) return;
    try { await api.delete(`/hosting/databases/${id}`); toast.success('DB ștearsă'); load(); }
    catch (e) { toast.error(e.response?.data?.error || 'Eroare'); }
  };

  const DB_COLORS = { mysql: '#4479a1', postgresql: '#336791', mongodb: '#47a248', redis: '#dc382d', sqlite: '#003b57' };

  return (
    <>
      <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10, marginBottom: 16 }}>
        <button className="btn btn-secondary" onClick={load}><RefreshCw size={15} /></button>
        <button className="btn btn-primary" onClick={() => setModal('new')}><Plus size={15} /> DB Nouă</button>
      </div>
      <div className="card" style={{ padding: 0 }}>
        {loading ? <div className="loading-center"><span className="spinner" /></div> : (
          <div className="table-wrapper"><table>
            <thead><tr><th>ETICHETĂ</th><th>TIP</th><th>HOST:PORT</th><th>BAZA DE DATE</th><th>CREDENȚIALE</th><th>VPS</th><th>CLIENT</th><th>EXPIRARE</th><th>STATUS</th><th>ACȚIUNI</th></tr></thead>
            <tbody>{list.map(d => (
              <tr key={d.id}>
                <td style={{ fontWeight: 600, fontSize: 13 }}>{d.name || d.database_name}</td>
                <td><span className="badge" style={{ background: `${DB_COLORS[d.type]}22`, color: DB_COLORS[d.type] }}>{d.type.toUpperCase()}</span></td>
                <td className="mono" style={{ fontSize: 12 }}>{d.host ? `${d.host}:${d.port}` : '—'}</td>
                <td className="mono" style={{ fontSize: 12 }}>{d.database_name}</td>
                <td style={{ fontSize: 12 }}><div>{d.username || '—'}</div><PasswordCell value={d.password} /></td>
                <td style={{ fontSize: 13 }}>{d.vps_name || '—'}</td>
                <td style={{ fontSize: 13 }}>{d.company_name || '—'}</td>
                <td><ExpiryCell date={d.expiry_date} /></td>
                <td><span className={`badge ${STATUS_CLASS[d.status] || 'badge-neutral'}`}>{STATUS_LABEL[d.status] || d.status}</span></td>
                <td><div style={{ display: 'flex', gap: 6 }}><button className="btn-icon" onClick={() => setModal(d)}><Edit2 size={14} /></button>{hasRole('superadmin', 'admin') && <button className="btn-icon" style={{ color: 'var(--danger)' }} onClick={() => del(d.id, d.database_name)}><Trash2 size={14} /></button>}</div></td>
              </tr>
            ))}</tbody>
          </table></div>
        )}
      </div>
      {modal && <DatabaseModal item={modal === 'new' ? null : modal} clients={clients} vpsList={vpsList} onClose={() => setModal(null)} onSave={() => { setModal(null); load(); }} />}
    </>
  );
}

// ─── SSL Tab ───────────────────────────────────────────────────────────────
function SslTab({ clients, vpsList }) {
  const [list, setList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modal, setModal] = useState(null);
  const { hasRole } = useAuth();
  const toast = useToast();

  const load = useCallback(async () => {
    setLoading(true);
    try { const { data } = await api.get('/hosting/ssl'); setList(data); }
    catch(e) { console.error('SSL load error', e); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const del = async (id, name) => {
    if (!window.confirm(`Ștergi SSL pentru "${name}"?`)) return;
    try { await api.delete(`/hosting/ssl/${id}`); toast.success('SSL șters'); load(); }
    catch (e) { toast.error(e.response?.data?.error || 'Eroare'); }
  };

  const SslModal = ({ item, onClose, onSave }) => {
    const [f, setF] = useState(item || { domain_name: '', issuer: '', type: 'letsencrypt', status: 'active', issued_date: '', expiry_date: '', auto_renew: 1, vps_id: '', client_id: '' });
    const [saving, setSaving] = useState(false);
    const toast2 = useToast();
    const set = (k, v) => setF(p => ({ ...p, [k]: v }));
    const save = async () => {
      setSaving(true);
      try {
        if (item?.id) { await api.put(`/hosting/ssl/${item.id}`, f); toast2.success('SSL actualizat'); }
        else { await api.post('/hosting/ssl', f); toast2.success('SSL adăugat'); }
        onSave();
      } catch (e) { toast2.error('Eroare'); } finally { setSaving(false); }
    };
    return (
      <div className="modal-overlay" onClick={e => e.target === e.currentTarget && onClose()}>
        <div className="modal modal-md">
          <div className="modal-header"><span className="modal-title">Certificat SSL</span><button className="btn-icon" onClick={onClose}>×</button></div>
          <div className="modal-body">
            <div className="form-grid form-grid-2">
              <div className="form-group"><label className="form-label">Domeniu</label><input className="form-input" value={f.domain_name} onChange={e => set('domain_name', e.target.value)} placeholder="exemplu.ro" /></div>
              <div className="form-group"><label className="form-label">Emitent</label><input className="form-input" value={f.issuer || ''} onChange={e => set('issuer', e.target.value)} placeholder="Let's Encrypt" /></div>
              <div className="form-group"><label className="form-label">Tip</label><select className="form-select" value={f.type} onChange={e => set('type', e.target.value)}><option value="letsencrypt">Let's Encrypt</option><option value="comodo">Comodo</option><option value="digicert">DigiCert</option><option value="self-signed">Self-signed</option><option value="other">Altul</option></select></div>
              <div className="form-group"><label className="form-label">Status</label><select className="form-select" value={f.status} onChange={e => set('status', e.target.value)}><option value="active">Activ</option><option value="expired">Expirat</option><option value="pending">Pending</option><option value="revoked">Revocat</option></select></div>
              <div className="form-group"><label className="form-label">Data Emiterii</label><input className="form-input" type="date" value={f.issued_date || ''} onChange={e => set('issued_date', e.target.value)} /></div>
              <div className="form-group"><label className="form-label">Data Expirării</label><input className="form-input" type="date" value={f.expiry_date || ''} onChange={e => set('expiry_date', e.target.value)} /></div>
              <div className="form-group"><label className="form-label">Auto Reînnoire</label><select className="form-select" value={f.auto_renew} onChange={e => set('auto_renew', parseInt(e.target.value))}><option value={1}>Da</option><option value={0}>Nu</option></select></div>
              <div className="form-group"><label className="form-label">VPS</label><select className="form-select" value={f.vps_id || ''} onChange={e => set('vps_id', e.target.value)}><option value="">—</option>{vpsList.map(v => <option key={v.id} value={v.id}>{v.name}</option>)}</select></div>
              <div className="form-group"><label className="form-label">Client</label><select className="form-select" value={f.client_id || ''} onChange={e => set('client_id', e.target.value)}><option value="">—</option>{clients.map(c => <option key={c.id} value={c.id}>{c.company_name || c.contact_name}</option>)}</select></div>
            </div>
          </div>
          <div className="modal-footer"><button className="btn btn-secondary" onClick={onClose}>Anulare</button><button className="btn btn-primary" onClick={save} disabled={saving}>{saving ? <span className="spinner" /> : 'Salvare'}</button></div>
        </div>
      </div>
    );
  };

  return (
    <>
      <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10, marginBottom: 16 }}>
        <button className="btn btn-secondary" onClick={load}><RefreshCw size={15} /></button>
        <button className="btn btn-primary" onClick={() => setModal('new')}><Plus size={15} /> SSL Nou</button>
      </div>
      <div className="card" style={{ padding: 0 }}>
        {loading ? <div className="loading-center"><span className="spinner" /></div> : (
          <div className="table-wrapper"><table>
            <thead><tr><th>DOMENIU</th><th>EMITENT</th><th>TIP</th><th>VPS</th><th>CLIENT</th><th>AUTO</th><th>EXPIRARE</th><th>STATUS</th><th>ACȚIUNI</th></tr></thead>
            <tbody>{list.map(s => (
              <tr key={s.id}>
                <td style={{ fontWeight: 600 }}>{s.domain_name}</td>
                <td style={{ fontSize: 13 }}>{s.issuer || '—'}</td>
                <td><span className="badge badge-info">{s.type}</span></td>
                <td style={{ fontSize: 13 }}>{s.vps_name || '—'}</td>
                <td style={{ fontSize: 13 }}>{s.company_name || '—'}</td>
                <td><span className={`badge ${s.auto_renew ? 'badge-success' : 'badge-neutral'}`}>{s.auto_renew ? 'Da' : 'Nu'}</span></td>
                <td><ExpiryCell date={s.expiry_date} /></td>
                <td><span className={`badge ${STATUS_CLASS[s.status] || 'badge-neutral'}`}>{STATUS_LABEL[s.status] || s.status}</span></td>
                <td><div style={{ display: 'flex', gap: 6 }}><button className="btn-icon" onClick={() => setModal(s)}><Edit2 size={14} /></button>{hasRole('superadmin', 'admin') && <button className="btn-icon" style={{ color: 'var(--danger)' }} onClick={() => del(s.id, s.domain_name)}><Trash2 size={14} /></button>}</div></td>
              </tr>
            ))}</tbody>
          </table></div>
        )}
      </div>
      {modal && <SslModal item={modal === 'new' ? null : modal} onClose={() => setModal(null)} onSave={() => { setModal(null); load(); }} />}
    </>
  );
}

// ─── IP Addresses Tab ──────────────────────────────────────────────────────
function IpsTab({ vpsList }) {
  const [list, setList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modal, setModal] = useState(null);
  const { hasRole } = useAuth();
  const toast = useToast();

  const load = useCallback(async () => {
    setLoading(true);
    try { const { data } = await api.get('/hosting/ips'); setList(data); }
    catch(e) { console.error('IPs load error', e); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const del = async (id, ip) => {
    if (!window.confirm(`Ștergi IP-ul "${ip}"?`)) return;
    try { await api.delete(`/hosting/ips/${id}`); toast.success('IP șters'); load(); }
    catch (e) { toast.error(e.response?.data?.error || 'Eroare'); }
  };

  const IpModal = ({ item, onClose, onSave }) => {
    const [f, setF] = useState(item || { ip: '', type: 'ipv4', is_dedicated: 0, rdns: '', vps_id: '', status: 'active', notes: '' });
    const [saving, setSaving] = useState(false);
    const toast2 = useToast();
    const set = (k, v) => setF(p => ({ ...p, [k]: v }));
    const save = async () => {
      if (!f.ip) return toast2.error('IP-ul este obligatoriu');
      setSaving(true);
      try {
        if (item?.id) { await api.put(`/hosting/ips/${item.id}`, f); toast2.success('IP actualizat'); }
        else { await api.post('/hosting/ips', f); toast2.success('IP adăugat'); }
        onSave();
      } catch (e) { toast2.error(e.response?.data?.error || 'Eroare'); } finally { setSaving(false); }
    };
    return (
      <div className="modal-overlay" onClick={e => e.target === e.currentTarget && onClose()}>
        <div className="modal modal-sm">
          <div className="modal-header"><span className="modal-title">Adresă IP</span><button className="btn-icon" onClick={onClose}>×</button></div>
          <div className="modal-body">
            <div className="form-group"><label className="form-label">Adresă IP *</label><input className="form-input" value={f.ip} onChange={e => set('ip', e.target.value)} placeholder="185.120.45.10" /></div>
            <div className="form-grid form-grid-2">
              <div className="form-group"><label className="form-label">Tip</label><select className="form-select" value={f.type} onChange={e => set('type', e.target.value)}><option value="ipv4">IPv4</option><option value="ipv6">IPv6</option></select></div>
              <div className="form-group"><label className="form-label">Dedicat</label><select className="form-select" value={f.is_dedicated} onChange={e => set('is_dedicated', parseInt(e.target.value))}><option value={1}>Da</option><option value={0}>Nu</option></select></div>
            </div>
            <div className="form-group"><label className="form-label">rDNS</label><input className="form-input" value={f.rdns || ''} onChange={e => set('rdns', e.target.value)} placeholder="server.domeniu.ro" /></div>
            <div className="form-group"><label className="form-label">VPS</label><select className="form-select" value={f.vps_id || ''} onChange={e => set('vps_id', e.target.value)}><option value="">—</option>{vpsList.map(v => <option key={v.id} value={v.id}>{v.name}</option>)}</select></div>
            <div className="form-group" style={{ marginBottom: 0 }}><label className="form-label">Notițe</label><textarea className="form-textarea" style={{ minHeight: 60 }} value={f.notes || ''} onChange={e => set('notes', e.target.value)} /></div>
          </div>
          <div className="modal-footer"><button className="btn btn-secondary" onClick={onClose}>Anulare</button><button className="btn btn-primary" onClick={save} disabled={saving}>{saving ? <span className="spinner" /> : 'Salvare'}</button></div>
        </div>
      </div>
    );
  };

  return (
    <>
      <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10, marginBottom: 16 }}>
        <button className="btn btn-secondary" onClick={load}><RefreshCw size={15} /></button>
        <button className="btn btn-primary" onClick={() => setModal('new')}><Plus size={15} /> IP Nou</button>
      </div>
      <div className="card" style={{ padding: 0 }}>
        {loading ? <div className="loading-center"><span className="spinner" /></div> : (
          <div className="table-wrapper"><table>
            <thead><tr><th>ADRESĂ IP</th><th>TIP</th><th>rDNS</th><th>VPS</th><th>DEDICAT</th><th>STATUS</th><th>ACȚIUNI</th></tr></thead>
            <tbody>{list.map(ip => (
              <tr key={ip.id}>
                <td className="mono" style={{ fontWeight: 600 }}>{ip.ip}</td>
                <td><span className="badge badge-info">{ip.type.toUpperCase()}</span></td>
                <td className="mono" style={{ fontSize: 12 }}>{ip.rdns || '—'}</td>
                <td style={{ fontSize: 13 }}>{ip.vps_name || '—'}</td>
                <td><span className={`badge ${ip.is_dedicated ? 'badge-success' : 'badge-neutral'}`}>{ip.is_dedicated ? 'Da' : 'Nu'}</span></td>
                <td><span className={`badge ${STATUS_CLASS[ip.status] || 'badge-neutral'}`}>{STATUS_LABEL[ip.status] || ip.status}</span></td>
                <td><div style={{ display: 'flex', gap: 6 }}><button className="btn-icon" onClick={() => setModal(ip)}><Edit2 size={14} /></button>{hasRole('superadmin', 'admin') && <button className="btn-icon" style={{ color: 'var(--danger)' }} onClick={() => del(ip.id, ip.ip)}><Trash2 size={14} /></button>}</div></td>
              </tr>
            ))}</tbody>
          </table></div>
        )}
      </div>
      {modal && <IpModal item={modal === 'new' ? null : modal} onClose={() => setModal(null)} onSave={() => { setModal(null); load(); }} />}
    </>
  );
}

// ─── Main Hosting Page ─────────────────────────────────────────────────────
export default function Hosting() {
  const [tab, setTab] = useState('vps');
  const [stats, setStats] = useState(null);
  const [clients, setClients] = useState([]);
  const [vpsList, setVpsList] = useState([]);

  useEffect(() => {
    let mounted = true;

    api.get('/hosting/stats')
      .then(r => { if (mounted) setStats(r.data); })
      .catch(e => console.error('Hosting stats load error', e));

    api.get('/clients')
      .then(r => { if (mounted) setClients(r.data); })
      .catch(e => console.error('Clients load error', e));

    api.get('/hosting/vps')
      .then(r => { if (mounted) setVpsList(r.data); })
      .catch(e => console.error('Hosting VPS list load error', e));

    return () => {
      mounted = false;
    };
  }, []);

  const TABS = [
    { key: 'vps', label: 'VPS', icon: Server },
    { key: 'domains', label: 'Domenii', icon: Globe },
    { key: 'databases', label: 'Baze de Date', icon: Database },
    { key: 'ssl', label: 'SSL', icon: Lock },
    { key: 'ips', label: 'IP Adrese', icon: Wifi },
  ];

  return (
    <div className="fade-in">
      <div className="page-header">
        <div><div className="page-title">Hosting</div><div className="page-subtitle">Gestiune infrastructură: VPS, domenii, baze de date, SSL, IP-uri</div></div>
      </div>

      {/* Stats row */}
      {stats && (
        <div className="stats-grid" style={{ marginBottom: 24 }}>
          <div className="stat-card"><div className="stat-icon" style={{ background: 'var(--success)20' }}><Server size={18} color="var(--success)" /></div><div className="stat-value" style={{ color: 'var(--success)' }}>{stats.vps}</div><div className="stat-label">VPS Active</div><div style={{ fontSize: 11, color: 'var(--text-secondary)', marginTop: 4 }}>{stats.monthlyCost?.toFixed(2)} EUR/lună</div></div>
          <div className="stat-card"><div className="stat-icon" style={{ background: 'var(--accent)20' }}><Globe size={18} color="var(--accent)" /></div><div className="stat-value" style={{ color: 'var(--accent)' }}>{stats.domains}</div><div className="stat-label">Domenii Active</div></div>
          <div className="stat-card"><div className="stat-icon" style={{ background: '#8b5cf620' }}><Database size={18} color="#8b5cf6" /></div><div className="stat-value" style={{ color: '#8b5cf6' }}>{stats.databases}</div><div className="stat-label">Baze de Date</div></div>
          <div className="stat-card"><div className="stat-icon" style={{ background: 'var(--warning)20' }}><AlertTriangle size={18} color="var(--warning)" /></div><div className="stat-value" style={{ color: stats.expiring7 > 0 ? 'var(--danger)' : 'var(--warning)' }}>{stats.expiring30}</div><div className="stat-label">Expiră în 30 zile</div><div style={{ fontSize: 11, color: stats.expiring7 > 0 ? 'var(--danger)' : 'var(--text-secondary)', marginTop: 4 }}>{stats.expiring7} în 7 zile !</div></div>
        </div>
      )}

      {/* Tabs */}
      <div className="tabs">
        {TABS.map(t => {
          const Icon = t.icon;
          return (
            <button key={t.key} className={`tab ${tab === t.key ? 'active' : ''}`} onClick={() => setTab(t.key)}>
              <Icon size={14} style={{ verticalAlign: 'middle', marginRight: 5 }} />{t.label}
            </button>
          );
        })}
      </div>

      {tab === 'vps' && <VpsTab clients={clients} />}
      {tab === 'domains' && <DomainsTab clients={clients} vpsList={vpsList} />}
      {tab === 'databases' && <DatabasesTab clients={clients} vpsList={vpsList} />}
      {tab === 'ssl' && <SslTab clients={clients} vpsList={vpsList} />}
      {tab === 'ips' && <IpsTab vpsList={vpsList} />}
    </div>
  );
}
